import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { ModalOpenProcessRef } from "@msys/ui";
import { Button, Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { useUserData } from "../../../auth/useUserData.js";
import {
  TemplateVariantUpdateProtocolEntryFragment,
  useTemplateVariantUpdateProtocolLazyQuery,
} from "./useTemplateVariantUpdateProtocol.generated.js";

export const useFetchUpdateProtocolEntries = () => {
  const client = useApolloClient();
  const [fetch, query] = useTemplateVariantUpdateProtocolLazyQuery({ client });
  const [loadingProtocolEntries, setLoadingProtocolEntries] =
    React.useState<boolean>(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t } = useTranslate(["Global", "Templates"]);
  const { hasOrganisationPermission } = useUserData();
  const showSeeMoreButton = hasOrganisationPermission(
    "template_document_update"
  );

  const updateVersionProtocolProcessRef = React.useRef<ModalOpenProcessRef>();

  const fetchEntries = React.useCallback(
    async ({
      docId,
      toVersionNumber,
      signal,
    }: {
      docId: string;
      toVersionNumber: number;
      signal?: AbortSignal;
    }): Promise<TemplateVariantUpdateProtocolEntryFragment[]> => {
      let entries;

      do {
        if (signal?.aborted) {
          throw new Error("Operation was aborted");
        }

        const result = await fetch({
          variables: {
            entryTemplateId: docId,
            toVersionNumber: toVersionNumber,
          },
          // don't provide a signal here and always finish query, since Apollo client throws an error
          // that signal is aborted without a reason
          // context: {
          //   fetchOptions: {
          //     signal,
          //   },
          // },
        });
        const protocolEntries = getDataOrNull(
          result.data?.templateVariantUpdateProtocol
        );
        if (protocolEntries?.protocol) {
          entries = protocolEntries.protocol.entries ?? [];
          break;
        }

        if (signal?.aborted) {
          throw new Error("Operation was aborted");
        }

        // sleep 1s before every new try
        await pause(1000, signal);
      } while (!entries);

      return entries;
    },
    [fetch]
  );

  const fetchEntriesOrFail = React.useCallback(
    async (opts: { docId: string; toVersionNumber: number }) => {
      setLoadingProtocolEntries(true);
      try {
        return await withTimeout(
          signal => fetchEntries({ ...opts, signal }),
          20_000
        );
      } finally {
        setLoadingProtocolEntries(false);
      }
    },
    [fetchEntries]
  );

  const checkUpdateProtocol = React.useCallback(
    async (opts: { docId: string; toVersionNumber: number }) => {
      try {
        const entries = await fetchEntriesOrFail(opts);
        const currentEntries = entries.filter(
          e => e.toVersionNumber === opts.toVersionNumber
        );
        if (currentEntries.length > 0) {
          const key = enqueueSnackbar(
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: "center",
              }}
            >
              <Typography>
                {t(
                  "Some of your template configurations had to be deleted because they were not compatible with the new version of the template",
                  { ns: "Templates" }
                )}
                {": "}
                {t(
                  "{number, plural, one {1 configuration was affected} other {# configurations were affected}}",
                  { ns: "Templates", number: currentEntries.length }
                )}
              </Typography>
              {showSeeMoreButton && (
                <Button
                  size="small"
                  color="inherit"
                  variant="outlined"
                  onClick={() => {
                    updateVersionProtocolProcessRef.current?.open({
                      toVersionNumber: opts.toVersionNumber,
                      entries: currentEntries,
                    });
                    closeSnackbar(key);
                  }}
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  {t("See more", { ns: "Global" })}
                </Button>
              )}
            </Stack>,
            { variant: "warning", persist: true }
          );
        }
      } catch (e) {
        if (e instanceof Error)
          enqueueSnackbar(e.message, { variant: "error" });
        Sentry.captureException(e);
      }
    },
    [closeSnackbar, enqueueSnackbar, fetchEntriesOrFail, showSeeMoreButton, t]
  );

  return {
    fetchProtocolEntries: fetchEntriesOrFail,
    checkUpdateProtocol,
    loadingProtocolEntries,
    updateVersionProtocolProcessRef,
  };
};

async function withTimeout<T>(
  promiseFactory: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number
): Promise<T> {
  const controller = new AbortController();

  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      controller.abort(); // Cancel the main operation
      reject(
        new Error(`Cannot fetch update protocol after ${timeoutMs / 1000}s`)
      );
    }, timeoutMs);
  });

  try {
    return await Promise.race([
      promiseFactory(controller.signal),
      timeoutPromise,
    ]);
  } catch (e) {
    controller.abort(); // Ensure cleanup if main promise throws
    throw e;
  }
}

function pause(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(resolve, ms);

    // Abort immediately when needed
    if (signal?.aborted) {
      clearTimeout(timeoutId);
      reject(new Error("Operation was aborted"));
      return;
    }

    // Listen for abort events
    const abortHandler = () => {
      clearTimeout(timeoutId);
      reject(new Error("Operation was aborted"));
    };

    signal?.addEventListener("abort", abortHandler, { once: true });
  });
}
